import React, { useState } from 'react';
import { signInWithEmailPassword, sendPassResetEmail } from '../services/firebase';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { api } from '../services/api';
import '../styles/forms.css';
import '../styles/Login.css';

const Login = ({ setUser }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [isRecoveringPassword, setIsRecoveringPassword] = useState(false);
  const [recoveryEmail, setRecoveryEmail] = useState('');
  const [noAccount, setNoAccount] = useState(false);
  const [signupHref, setSignupHref] = useState(() => {
    const t = new URLSearchParams(window.location.search).get('invite_token');
    return t ? `/signup?invite_token=${encodeURIComponent(t)}` : '/signup';
  });

  const navigate = useNavigate();
  const location = useLocation();

  const getFirebaseErrorMessage = (errorCode) => {
    switch (errorCode) {
      case 'auth/email-already-in-use':
        return 'The email address is already in use. Please use a different email.';
      case 'auth/invalid-email':
        return 'The email address is invalid. Please enter a valid email.';
      case 'auth/weak-password':
        return 'The password is too weak. Please choose a stronger password.';
      case 'auth/user-not-found':
        return 'No user found with this email address. Please sign up first.';
      case 'auth/wrong-password':
        return 'The password is incorrect. Please try again.';
      case 'auth/invalid-credential':
        return 'Invalid credentials. Please check email and password. Passwords are case-sensitive.';
      case 'auth/network-request-failed':
        return 'A network error occurred. Please check your internet connection and try again.';
      default:
        return 'An unexpected error occurred. Please try again later.';
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    let credentials;

    try {
      credentials = await signInWithEmailPassword(email, password);
      const idToken = await credentials.getIdToken();
      const response = await api.post('signin', {
        email: email,
        external_id: credentials.uid,
        id_token: idToken,
      });

      if (response.status !== 200) {
        throw new Error('Failed to sync user with backend');
      }

      const data = await response.data;
      const { token, settings, role } = data;
      localStorage.setItem('token', token);
      localStorage.setItem('userSettings', JSON.stringify(settings));
      localStorage.setItem('role', JSON.stringify(role));

      setUser(credentials);

      console.log('User logged in successfully');
      navigate('/dashboard');
    } catch (error) {
      console.error('Error during login:', error);
      // Backend returned 404: the Firebase user authenticated fine, but no
      // matching User row exists in the DB — i.e. this is a new account that
      // needs to go through signup instead of signin. Surface a clear message
      // and a link to signup that preserves any invite_token in the URL.
      if (error.response?.status === 404) {
        setNoAccount(true);
        const params = new URLSearchParams(location.search);
        const inviteToken = params.get('invite_token');
        setSignupHref(inviteToken ? `/signup?invite_token=${encodeURIComponent(inviteToken)}` : '/signup');
        setError(`No account found for ${email}. You need to sign up first.`);
      } else {
        setNoAccount(false);
        const errorCode = error.code;
        const errorMessage = getFirebaseErrorMessage(errorCode);
        setError(errorMessage);
      }
    } finally {
      setLoading(false);
    }
  }

  const handleRecoverPassword = async () => {
    if (!recoveryEmail) {
      setError('Please enter your email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await sendPassResetEmail(recoveryEmail);
      setError('Password reset email sent. Please check your inbox.');
      setIsRecoveringPassword(false);
    } catch (error) {
      console.error('Error sending password reset email:', error);
      const errorCode = error.code;
      const errorMessage = getFirebaseErrorMessage(errorCode);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className='card'>
        <h1>Login</h1>
        {error && (
          <p className='error'>
            {error}
            {noAccount && (
              <>
                {' '}
                <Link to={signupHref} className="link-button">Sign up here</Link>
              </>
            )}
          </p>
        )}
        {!isRecoveringPassword ? (
          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label>Email
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </label>
            </div>
            <div className="form-group">
              <label>Password
                <input
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </label>

              <p className="forgot-password-link">
                <button
                  type="button"
                  className="link-button"
                  onClick={() => {
                    setError('');
                    setIsRecoveringPassword(true);
                  }}
                >
                  Forgot your password?
                </button>
              </p>
            </div>

            <button type="submit" disabled={loading}>
              {loading ? 'Logging in...' : 'Login'}
            </button>

            <p className="forgot-password-link">
              Don't have an account?{' '}
              <Link to={signupHref} className="link-button">Sign Up</Link>
            </p>
          </form>
        ) : (
          <div>
            <h2>Recover Password</h2>
            {error && <p className='error'>{error}</p>}
            <div className="form-group">
              <input
                type="email"
                placeholder="Enter your email"
                value={recoveryEmail}
                onChange={(e) => setRecoveryEmail(e.target.value)}
              />
            </div>

            <div className="form-group-row">
              <button
                onClick={handleRecoverPassword}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Email'}
              </button>

              <button
                onClick={() => setIsRecoveringPassword(false)}
                className="cancel"
              >
                Cancel
              </button>
 
            </div>
          </div>
        )}
      </div>
    </div >
  );
};

export default Login;
